import { useEffect, useRef, useState } from "react";
import {
    SquaresFour, Plus, CheckCircle, X, Lightning,
    Clock, Lock, Gear, Hash,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatINR } from "../../lib/format";
import FormDialog, { FormDialogCancel } from "../../components/FormDialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import RoleGuard from "../../components/RoleGuard";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { cn as classJoin } from "../../lib/utils";

const STATUS_TONE = {
    Pending: "bg-slate-50 text-slate-700 border-slate-200",
    Approved: "bg-indigo-50 text-indigo-700 border-indigo-200",
    Merged: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Rejected: "bg-rose-50 text-rose-700 border-rose-200",
};

const STATUS_ICON = {
    Pending: <Clock size={10} weight="fill" />,
    Approved: <CheckCircle size={10} weight="fill" />,
    Merged: <Lightning size={10} weight="fill" />,
    Rejected: <X size={10} weight="fill" />,
};

export default function CustomizationsCard({ proposalId, proposalStatus, onChanged }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [busyId, setBusyId] = useState(null);

    const draftLocked = proposalStatus !== "Draft";

    const load = async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get(`/proposals/${proposalId}/customizations`);
            setData(d);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load scoping items"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [proposalId]);

    const approve = async (id) => {
        setBusyId(id);
        try {
            await api.post(`/proposals/${proposalId}/customizations/${id}/approve`, { reason: "" });
            toast.success("Scoping item approved");
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Approve failed"));
        } finally {
            setBusyId(null);
        }
    };

    const reject = async (id) => {
        setBusyId(id);
        try {
            await api.post(`/proposals/${proposalId}/customizations/${id}/reject`, { reason: "" });
            toast.success("Scoping item rejected");
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Reject failed"));
        } finally {
            setBusyId(null);
        }
    };

    const merge = async (id) => {
        if (draftLocked) {
            toast.error("Proposal is locked — scoping items cannot be merged after Draft.");
            return;
        }
        setBusyId(id);
        try {
            await api.post(`/proposals/${proposalId}/customizations/${id}/merge`);
            toast.success("Merged into proposal as a new line item");
            await load();
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err, "Merge failed"));
        } finally {
            setBusyId(null);
        }
    };

    if (loading && !data) return null;
    if (!data) return null;

    const totals = data.totals || {};

    return (
        <div className="reedos-card" data-testid={`customizations-card-${proposalId}`}>
            <div className="px-5 py-4 border-b border-slate-200">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <div className="reedos-eyebrow flex items-center gap-1.5">
                            <SquaresFour size={11} /> Scoping Items
                            {draftLocked && (
                                <span className="inline-flex items-center gap-1 rounded bg-amber-50 border border-amber-200 text-amber-800 px-1.5 py-px text-[9px] font-semibold">
                                    <Lock size={9} weight="fill" /> Merge locked ({proposalStatus})
                                </span>
                            )}
                        </div>
                        <h3 className="font-display text-base font-semibold text-slate-900 mt-0.5">
                            {data.total} scoping item{data.total === 1 ? "" : "s"}
                        </h3>
                        <div className="text-[11px] text-slate-500 mt-1">
                            Pre-sales scoping work — track requested features with effort &amp; cost. Only <strong>Approved</strong> items in a <strong>Draft</strong> proposal can be merged into commercial flow as line items.
                        </div>
                    </div>
                    <RoleGuard permission="proposal:write">
                        <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)} className="gap-1.5 shrink-0" data-testid={`customization-new-btn-${proposalId}`}>
                            <Plus size={12} /> New
                        </Button>
                    </RoleGuard>
                </div>
                {/* Totals strip */}
                <div className="grid grid-cols-3 gap-3 mt-4">
                    <Metric label="Pending" value={formatINR(totals.pending_cost || 0)} count={data.counts.Pending || 0} tone="slate" />
                    <Metric label="Approved" value={formatINR(totals.approved_cost || 0)} count={data.counts.Approved || 0} tone="indigo" />
                    <Metric label="Merged" value={formatINR(totals.merged_cost || 0)} count={data.counts.Merged || 0} tone="emerald" />
                </div>
            </div>

            {data.items.length === 0 ? (
                <div className="px-5 py-10 text-center text-sm text-slate-500" data-testid={`customizations-empty-${proposalId}`}>
                    No scoping items captured yet.
                </div>
            ) : (
                <div className="divide-y divide-slate-100">
                    {data.items.map((c) => (
                        <div key={c.id} className="px-5 py-3 flex items-start gap-3" data-testid={`customization-row-${c.id}`}>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className={classJoin("inline-flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-semibold border", STATUS_TONE[c.status])}>
                                        {STATUS_ICON[c.status]} {c.status}
                                    </span>
                                    <div className="text-sm font-semibold text-slate-900 truncate">{c.description}</div>
                                </div>
                                <div className="text-[11px] text-slate-600 mt-1 flex items-center gap-3 flex-wrap">
                                    <span className="font-mono"><Hash size={10} className="inline mr-0.5" />{formatINR(c.cost_impact)}</span>
                                    {c.effort_hours ? <span className="font-mono"><Gear size={10} className="inline mr-0.5" />{c.effort_hours}h</span> : null}
                                    {c.gst_percent ? <span className="text-slate-500">GST {c.gst_percent}%</span> : null}
                                </div>
                                {c.notes && <div className="text-[11px] text-slate-500 mt-1 whitespace-pre-wrap">{c.notes}</div>}
                            </div>
                            <RoleGuard permission="proposal:write">
                                <div className="flex items-center gap-1 shrink-0">
                                    {c.status === "Pending" && (
                                        <>
                                            <Button size="sm" variant="outline" onClick={() => approve(c.id)} disabled={busyId === c.id} className="h-7 text-[10px] px-2 gap-1 border-indigo-200 text-indigo-700" data-testid={`customization-approve-${c.id}`}>
                                                <CheckCircle size={10} weight="fill" /> Approve
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={() => reject(c.id)} disabled={busyId === c.id} className="h-7 text-[10px] px-2 gap-1 text-rose-700" data-testid={`customization-reject-${c.id}`}>
                                                <X size={10} /> Reject
                                            </Button>
                                        </>
                                    )}
                                    {c.status === "Approved" && !draftLocked && (
                                        <Button size="sm" onClick={() => merge(c.id)} disabled={busyId === c.id} className="h-7 text-[10px] px-2 gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid={`customization-merge-${c.id}`}>
                                            <Lightning size={10} weight="fill" /> Merge
                                        </Button>
                                    )}
                                    {c.status === "Approved" && draftLocked && (
                                        <span className="text-[10px] text-amber-700 inline-flex items-center gap-1" title="Proposal is no longer Draft — merge is locked.">
                                            <Lock size={10} /> Locked
                                        </span>
                                    )}
                                </div>
                            </RoleGuard>
                        </div>
                    ))}
                </div>
            )}

            <NewCustomizationDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                proposalId={proposalId}
                onCreated={() => { setCreateOpen(false); load(); }}
            />
        </div>
    );
}

function Metric({ label, value, count, tone }) {
    const toneColors = {
        slate: "text-slate-700", indigo: "text-indigo-700", emerald: "text-emerald-700",
    };
    return (
        <div className="text-center">
            <div className={classJoin("reedos-eyebrow text-[9px]", toneColors[tone])}>{label}</div>
            <div className="text-sm font-mono font-bold text-slate-900 mt-0.5">{value}</div>
            <div className="text-[10px] text-slate-500">{count} item{count === 1 ? "" : "s"}</div>
        </div>
    );
}

function NewCustomizationDialog({ open, onClose, proposalId, onCreated }) {
    const blank = { description: "", effort_hours: "", cost_impact: "", gst_percent: 18, notes: "" };
    const [form, setForm] = useState(blank);
    const [busy, setBusy] = useState(false);
    const baselineRef = useRef(JSON.stringify(blank));

    useEffect(() => {
        if (open) {
            setForm(blank);
            baselineRef.current = JSON.stringify(blank);
        }
        // eslint-disable-next-line
    }, [open]);

    const isDirty = JSON.stringify(form) !== baselineRef.current;

    const submit = async () => {
        if (!form.description || !form.cost_impact) {
            toast.error("Description + cost impact are required");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/proposals/${proposalId}/customizations/create`, {
                description: form.description,
                effort_hours: form.effort_hours ? Number(form.effort_hours) : undefined,
                cost_impact: Number(form.cost_impact),
                gst_percent: Number(form.gst_percent || 18),
                notes: form.notes,
            });
            toast.success("Scoping item created");
            baselineRef.current = JSON.stringify(form);  // bypass discard prompt
            onCreated();
        } catch (err) {
            toast.error(formatApiError(err, "Failed to create"));
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;
    return (
        <FormDialog
            open={open}
            onOpenChange={(o) => { if (!o) onClose(); }}
            isDirty={isDirty}
            contentClassName="sm:max-w-md"
            testId="customization-dialog"
            title={<span className="flex items-center gap-2"><SquaresFour size={18} weight="duotone" className="text-indigo-600" />New scoping item</span>}
            description="Capture a scope item the client has requested — feature, integration, or custom build. Nothing touches the proposal's commercial values until you explicitly Approve and then Merge it as a line item."
        >
                <div className="space-y-3">
                    <div>
                        <Label className="text-[10px] font-semibold text-slate-500 uppercase">Description *</Label>
                        <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Custom integration with their ERP" className="mt-1 h-9 text-sm" data-testid="customization-field-description" />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label className="text-[10px] font-semibold text-slate-500 uppercase">Effort (hrs)</Label>
                            <Input type="number" min="0" step="1" value={form.effort_hours} onChange={(e) => setForm({ ...form, effort_hours: e.target.value })} className="mt-1 h-9 text-sm font-mono" data-testid="customization-field-effort" />
                        </div>
                        <div>
                            <Label className="text-[10px] font-semibold text-slate-500 uppercase">Cost (₹) *</Label>
                            <Input type="number" min="0" step="1000" value={form.cost_impact} onChange={(e) => setForm({ ...form, cost_impact: e.target.value })} className="mt-1 h-9 text-sm font-mono" data-testid="customization-field-cost" />
                        </div>
                        <div>
                            <Label className="text-[10px] font-semibold text-slate-500 uppercase">GST %</Label>
                            <select value={form.gst_percent} onChange={(e) => setForm({ ...form, gst_percent: e.target.value })} className="mt-1 w-full h-9 px-2 text-sm border border-slate-200 rounded bg-white" data-testid="customization-field-gst">
                                {[0, 5, 12, 18, 28].map((r) => <option key={r} value={r}>{r}%</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <Label className="text-[10px] font-semibold text-slate-500 uppercase">Notes</Label>
                        <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1 w-full px-3 py-2 text-sm border border-slate-200 rounded resize-none" data-testid="customization-field-notes" />
                    </div>
                </div>
                <DialogFooter>
                    <FormDialogCancel variant="ghost" testId="customization-cancel">Cancel</FormDialogCancel>
                    <Button onClick={submit} disabled={busy} data-testid="customization-submit">
                        {busy ? "Saving…" : "Save"}
                    </Button>
                </DialogFooter>
        </FormDialog>
    );
}
